import React from 'react';
import { Link } from 'react-router';
import SEO from '../components/SEO';
import GlowCard from '../components/GlowCard';
import AIImage from '../components/AIImage';
import { trackEvent } from '../components/analytics';

const Solutions: React.FC = () => {
  return (
    <div className="animate-fade-in-up">
      <SEO
        title="Aviation Software Solutions for CAAs, ANSPs, Airlines, and Operators"
        description="Aviation software solutions for Civil Aviation Authorities, ANSPs, airports, airlines, and regulated operators using SMS, LMS, eAIP, PANS-OPS, and governance workflows."
      />

      <section className="py-24 bg-slate-950 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h1 className="text-5xl lg:text-7xl font-bold font-heading mb-8 leading-tight">
              Aviation software solutions for <br />
              <span className="text-accent">regulated organizations</span>
            </h1>
            <p className="text-xl text-slate-400 mb-6 leading-relaxed">
              AviaGov adapts to the structure, responsibilities, and oversight needs of aviation authorities, operators, and institutional stakeholders.
            </p>
            <p className="text-slate-400 text-lg leading-relaxed">
              One platform, used differently by role. Each organization activates only the modules relevant to its mandate, with governance, validation, and human responsibility preserved at every step.
            </p>
            <p className="text-slate-400 text-base leading-relaxed mt-4">
              Each deployment maps software modules to operational responsibility, approval workflows, and the infrastructure model required by the organization.
            </p>
          </div>
          <div className="relative p-1 bg-slate-800 rounded-2xl overflow-hidden border border-white/10">
            <AIImage
              prompt="A clean, isometric architecture diagram of an aviation compliance platform showing core platform with modules labeled SMS, LMS, eAIP, PANS-OPS, and FlyMate, connected by governance and oversight layers, professional blueprint style, minimal color accents"
              alt="AviaGov platform architecture overview"
              className="w-full opacity-95"
              aspectRatio="16:9"
              sizes="(min-width: 1024px) 50vw, 100vw"
              width={1600}
              height={900}
              loading="eager"
              fetchPriority="high"
            />
          </div>
        </div>
      </section>

      <section className="py-24 px-4 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-12 text-white">Solutions by Organization</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <GlowCard className="p-10">
              <h3 className="text-2xl font-bold text-white mb-4">Civil Aviation Authority (CAA)</h3>
              <ul className="text-slate-400 text-sm space-y-2 mb-6">
                <li>Regulatory oversight and compliance enforcement</li>
                <li>Safety governance and audit coordination</li>
                <li>Aeronautical information publication</li>
                <li>Procedure approval and oversight</li>
              </ul>
              <p className="text-slate-300 text-sm mb-4"><strong>Modules:</strong> SMS (oversight), LMS (training oversight), eAIP, PANS-OPS, FlyMate (optional)</p>
              <p className="text-slate-400 text-sm mb-4">
                Typical setup: private cloud or on-premise with dedicated authority governance workflows and approval gates.
              </p>
              <p className="text-slate-300 text-sm">
                Outcome: traceable oversight across safety, training, and aeronautical information with authority validation.
              </p>
              <Link to="/lms-ansp-caa" className="inline-flex mt-6 text-accent font-bold hover:text-white transition">
                Explore LMS for ANSPs and CAAs
              </Link>
            </GlowCard>

            <GlowCard className="p-10">
              <h3 className="text-2xl font-bold text-white mb-4">Air Navigation Service Provider (ANSP)</h3>
              <ul className="text-slate-400 text-sm space-y-2 mb-6">
                <li>Operational safety management and reporting</li>
                <li>Training oversight for operational staff</li>
                <li>Aeronautical information publication</li>
                <li>Procedure design and maintenance</li>
              </ul>
              <p className="text-slate-300 text-sm mb-4"><strong>Modules:</strong> SMS, LMS, eAIP, PANS-OPS, FlyMate (optional)</p>
              <p className="text-slate-400 text-sm mb-4">
                Typical setup: private cloud with role-based access control and operational segregation.
              </p>
              <p className="text-slate-300 text-sm">
                Outcome: integrated safety, training, and publication workflows with auditable controls.
              </p>
              <Link to="/lms-ansp-caa" className="inline-flex mt-6 text-accent font-bold hover:text-white transition">
                Explore LMS for ANSPs and CAAs
              </Link>
            </GlowCard>

            <GlowCard className="p-10">
              <h3 className="text-2xl font-bold text-white mb-4">Airport Operator</h3>
              <ul className="text-slate-400 text-sm space-y-2 mb-6">
                <li>Airport safety reporting and assurance</li>
                <li>Staff training and competency oversight</li>
                <li>Procedure coordination where applicable</li>
              </ul>
              <p className="text-slate-300 text-sm mb-4"><strong>Modules:</strong> SMS, LMS, PANS-OPS (only if applicable), FlyMate (optional)</p>
              <p className="text-slate-400 text-sm mb-4">
                Typical setup: public or private cloud with airport-specific safety governance and training records.
              </p>
              <p className="text-slate-300 text-sm">
                Outcome: regulator-ready safety and training evidence with controlled procedure support.
              </p>
            </GlowCard>

            <GlowCard className="p-10">
              <h3 className="text-2xl font-bold text-white mb-4">Airline</h3>
              <ul className="text-slate-400 text-sm space-y-2 mb-6">
                <li>Operational SMS implementation</li>
                <li>Training and competency tracking</li>
                <li>Documented compliance evidence</li>
              </ul>
              <p className="text-slate-300 text-sm mb-4"><strong>Modules:</strong> SMS, LMS, DMS, FlyMate (optional)</p>
              <p className="text-slate-400 text-sm mb-4">
                In airline operations, safety findings identified in the SMS can trigger targeted training
                actions in the LMS. Once completed, training records feed back into the SMS as mitigation
                and safety promotion evidence, creating a closed, auditable compliance loop.
              </p>
              <p className="text-slate-400 text-sm mb-4">
                eAIP and PANS-OPS are not applicable to airline operations.
              </p>
              <p className="text-slate-300 text-sm">
                Outcome: auditable safety and training records aligned to operational oversight requirements.
              </p>
              <Link to="/lms-airlines" className="inline-flex mt-6 text-accent font-bold hover:text-white transition">
                Explore LMS for Airlines
              </Link>
            </GlowCard>

            <GlowCard className="p-10">
              <h3 className="text-2xl font-bold text-white mb-4">Regulated Aviation Company</h3>
              <ul className="text-slate-400 text-sm space-y-2 mb-6">
                <li>SMS implementation for regulated activities</li>
                <li>Training compliance and competency tracking</li>
                <li>Controlled external training access when required</li>
              </ul>
              <p className="text-slate-300 text-sm mb-4"><strong>Modules:</strong> SMS, LMS, optional controlled external training access</p>
              <p className="text-slate-400 text-sm mb-4">
                Typical setup: public or private cloud with organization-defined access and certification scope.
              </p>
              <p className="text-slate-300 text-sm">
                Outcome: standardized safety and training governance aligned with regulatory expectations.
              </p>
              <Link to="/lms-aviation-companies" className="inline-flex mt-6 text-accent font-bold hover:text-white transition">
                Explore LMS for Aviation Companies
              </Link>
            </GlowCard>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 bg-slate-950/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-8 text-white">Infrastructure & Deployment</h2>
          <p className="text-slate-400 text-base leading-relaxed mb-8">
            AviaGov supports Public Cloud, Private Cloud, and On-Premise deployments to align with national data policies and organizational governance.
          </p>
          <ul className="text-slate-400 text-sm space-y-2">
            <li>Organization-level data isolation</li>
            <li>Role-based access control</li>
            <li>Human approval for all safety and regulatory decisions</li>
          </ul>
        </div>
      </section>

      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <p className="text-slate-400 text-base leading-relaxed">
            AviaGov supports both implementation and oversight within the same ecosystem without blurring responsibilities. Airlines implement and demonstrate compliance, authorities oversee and validate, and ANSPs operate within clearly governed institutional environments. Each organization activates only the modules relevant to its role.
          </p>
        </div>
      </section>

      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact" className="px-10 py-4 border border-white/10 text-white font-bold rounded-xl hover:bg-white/5" onClick={() => trackEvent('generate_lead', { form_id: 'solutions_discuss_setup' })}>
              Discuss Your Organization’s Setup
            </Link>
            <Link to="/contact" className="px-10 py-4 bg-accent text-primary font-bold rounded-xl" onClick={() => trackEvent('demo_request', { form_id: 'solutions_request_demo' })}>
              Request a Demo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Solutions;
